const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Builds the theme's cart update event.
 * @param {object | undefined} resource - The cart resource
 * @param {string} sourceId - The event source ID
 * @param {object} data - The event detail data
 * @returns {Event}
 */
function createCartAddEvent(resource, sourceId, data) {
  const event = new Event('cart:update', { bubbles: true });
  Object.assign(event, { detail: { resource, sourceId, data: { ...data } } });
  return event;
}

const ROOT_SELECTOR = '[data-sf-homepage]';

/**
 * Collects the homepage roots inside a scope.
 * @param {Document | Element} [scope] - The scope to search
 * @returns {Element[]}
 */
function homepageRoots(scope = document) {
  /** @type {Element[]} */
  const roots = [];
  if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
  roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
  return roots;
}

/** @type {Set<string>} */
const scrollLocks = new Set();

/** @param {string} token - The lock token */
function lockScroll(token) {
  scrollLocks.add(token);
  document.documentElement.classList.add('sf-homepage-scroll-locked', token);
}

/** @param {string} token - The lock token */
function unlockScroll(token) {
  scrollLocks.delete(token);
  document.documentElement.classList.remove(token);
  if (scrollLocks.size === 0) document.documentElement.classList.remove('sf-homepage-scroll-locked');
}

/** @type {IntersectionObserver | null} */
let revealObserver = null;

function getRevealObserver() {
  if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        revealObserver?.unobserve(entry.target);
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  return revealObserver;
}

/** @param {Document | Element} [scope] - The scope to initialise */
function initReveals(scope = document) {
  const observer = getRevealObserver();

  for (const root of homepageRoots(scope)) {
    for (const target of root.querySelectorAll('.sf-reveal:not(.is-revealed)')) {
      if (prefersReducedMotion.matches || !observer) {
        target.classList.add('is-revealed');
      } else {
        observer.observe(target);
      }
    }
  }
}

class HeroSlideshow extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #index = 0;

  /** @type {Element[]} */
  slides = [];

  /** @type {Element[]} */
  dots = [];

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-sf-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-sf-hero-dot]'));

    if (this.slides.length < 2) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.#goTo(index);
        this.#start();
      });
    });

    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onVisibilityChange = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };

  /** @param {number} index - The slide index to show */
  #goTo(index) {
    const next = (index + this.slides.length) % this.slides.length;

    this.slides[this.#index]?.classList.remove('is-active');
    this.dots[this.#index]?.classList.remove('is-active');

    this.#index = next;

    this.slides[next]?.classList.add('is-active');
    this.dots[next]?.classList.add('is-active');
  }

  #start = () => {
    if (this.dataset.autoplay !== 'true' || prefersReducedMotion.matches) return;

    this.#stop();

    const seconds = Number(this.dataset.autoplaySpeed) || 6;
    this.#timer = window.setInterval(() => this.#goTo(this.#index + 1), seconds * 1000);
  };

  #stop = () => {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = undefined;
    }
  };
}

class PillarExplorer extends HTMLElement {
  /** @type {Element[]} */
  panels = [];

  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('[data-sf-pillar]'));

    for (const panel of this.panels) {
      panel.addEventListener('mouseenter', () => this.#open(panel));
      panel.addEventListener('focus', () => this.#open(panel));
      panel.addEventListener('click', (event) => {
        if (window.matchMedia('(hover: hover)').matches) return;
        if (panel.classList.contains('is-open')) return;
        event.preventDefault();
        this.#open(panel);
      });
    }
  }

  /** @param {Element} target - The panel to open */
  #open(target) {
    for (const panel of this.panels) {
      panel.classList.toggle('is-open', panel === target);
    }
  }
}

class VideoFeature extends HTMLElement {
  controller = new AbortController();

  connectedCallback() {
    this.controller = new AbortController();
    this.#initNativeVideo();
    this.#initFacade();
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  #initNativeVideo() {
    const video = this.querySelector('video');
    if (!video) return;

    const play = () => video.play().catch(() => {});
    const clickToPlay = this.dataset.playback === 'click';

    if (clickToPlay) {
      this.#initClickToPlay(video, play);
    } else {
      play();
      // Keeps the reference's "always running" background video: resist pausing
      // and pick playback back up when the tab is shown again.
      video.addEventListener('pause', play, { signal: this.controller.signal });
      document.addEventListener(
        'visibilitychange',
        () => {
          if (!document.hidden) play();
        },
        { signal: this.controller.signal }
      );
    }

    const soundToggle = this.querySelector('[data-sf-video-sound]');
    if (!soundToggle) return;

    soundToggle.addEventListener(
      'click',
      () => {
        video.muted = !video.muted;
        soundToggle.setAttribute('aria-pressed', String(!video.muted));
        soundToggle.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        // Only chase playback in autoplay mode; in click-to-play a paused video
        // must stay paused when the visitor only meant to change the sound.
        if (!clickToPlay || !video.paused) play();
      },
      { signal: this.controller.signal }
    );
  }

  /**
   * Click-to-play mode: no autoplay, the poster holds until the visitor presses
   * the play control (or taps/keys the button covering the video).
   * @param {HTMLVideoElement} video
   * @param {() => void} play
   */
  #initClickToPlay(video, play) {
    const trigger = this.querySelector('[data-sf-video-play]');
    const { signal } = this.controller;

    video.addEventListener(
      'play',
      () => {
        this.classList.add('is-playing');
        trigger?.setAttribute('aria-label', 'Pause video');
      },
      { signal }
    );

    video.addEventListener(
      'pause',
      () => {
        this.classList.remove('is-playing');
        trigger?.setAttribute('aria-label', 'Play video');
      },
      { signal }
    );

    trigger?.addEventListener(
      'click',
      () => {
        if (video.paused) play();
        else video.pause();
      },
      { signal }
    );
  }

  #initFacade() {
    const facade = this.querySelector('[data-sf-video-facade]');
    const template = this.querySelector('[data-sf-video-embed]');
    if (!facade || !(template instanceof HTMLTemplateElement)) return;

    facade.addEventListener(
      'click',
      () => {
        this.appendChild(template.content.cloneNode(true));
        facade.remove();
      },
      { once: true }
    );
  }
}

class VideoMarquee extends HTMLElement {
  connectedCallback() {
    if (this.dataset.pauseOnHover === 'true') {
      this.addEventListener('mouseenter', () => this.setAttribute('data-paused', 'true'));
      this.addEventListener('mouseleave', () => this.removeAttribute('data-paused'));
    }

    for (const card of this.querySelectorAll('[data-sf-video-id]')) {
      this.#bindPreview(card);
    }
  }

  /** @param {Element} card - The video card to bind a hover preview to */
  #bindPreview(card) {
    const thumb = card.querySelector('.sf-tube__thumb');
    const videoId = card.getAttribute('data-sf-video-id');
    if (!thumb || !videoId) return;

    /** @type {HTMLIFrameElement | null} */
    let frame = null;

    card.addEventListener('mouseenter', () => {
      if (frame || prefersReducedMotion.matches) return;

      frame = document.createElement('iframe');
      frame.title = card.querySelector('.sf-tube__title')?.textContent?.trim() || 'Video preview';
      frame.setAttribute('allow', 'autoplay; encrypted-media');
      frame.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0&controls=1&playsinline=1`;
      thumb.appendChild(frame);
    });

    card.addEventListener('mouseleave', () => {
      frame?.remove();
      frame = null;
    });
  }
}

class AnnouncementBar extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #index = 0;

  /** @type {Element[]} */
  slides = [];

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-sf-topbar-slide]'));
    if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || prefersReducedMotion.matches) return;

    const seconds = Number(this.dataset.autoplaySpeed) || 5;
    this.#timer = window.setInterval(() => this.#advance(), seconds * 1000);
  }

  disconnectedCallback() {
    this.#pause();
  }

  #advance() {
    const current = this.slides[this.#index];
    current?.classList.remove('is-active');
    current?.setAttribute('aria-hidden', 'true');

    this.#index = (this.#index + 1) % this.slides.length;

    const next = this.slides[this.#index];
    next?.classList.add('is-active');
    next?.removeAttribute('aria-hidden');
  }

  #pause = () => {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = undefined;
    }
  };
}

const SOLIDIFY_AFTER = 40;

const NAV_GAP_STEPS = [26, 22, 18, 15, 12];

class SiteHeader extends HTMLElement {
  /** @type {HTMLElement | null} */
  #lastFocused = null;

  /** @type {Element | null} */
  drawer = null;

  /** @type {Element | null} */
  scrim = null;

  /** @type {ResizeObserver | undefined} */
  observer;

  connectedCallback() {
    this.drawer = this.querySelector('[data-sf-nav-drawer]');
    this.scrim = this.querySelector('.sf-header__scrim');

    this.querySelector('[data-sf-nav-open]')?.addEventListener('click', () => this.openDrawer());
    for (const button of this.querySelectorAll('[data-sf-nav-close]')) {
      button.addEventListener('click', () => this.closeDrawer());
    }
    for (const link of this.querySelectorAll('.sf-header__drawer a')) {
      link.addEventListener('click', () => this.closeDrawer());
    }

    document.addEventListener('keydown', this.#onKeydown);

    // Delegated on the header itself, so it is torn down with the element and
    // survives Theme Editor section re-renders without stacking listeners.
    this.addEventListener('click', this.#onActionClick);

    if (this.dataset.transparent === 'true') {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#onScroll();
    }

    this.#measure();
    this.#fitNav();
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => requestAnimationFrame(this.#onResize));
      this.observer.observe(this);
      const actions = this.querySelector('.sf-header__actions');
      if (actions) this.observer.observe(actions);
    }
    window.addEventListener('resize', this.#onResize, { passive: true });
    document.fonts?.ready.then(() => this.#fitNav());
    window.addEventListener('load', this.#onResize, { once: true, passive: true });

    for (const dropdown of this.querySelectorAll('[data-sf-nav-dropdown]')) {
      dropdown.addEventListener('mouseleave', () => dropdown.removeAttribute('open'));
      dropdown.addEventListener('focusout', (event) => {
        const related = /** @type {FocusEvent} */ (event).relatedTarget;
        if (!(related instanceof Node) || !dropdown.contains(related)) dropdown.removeAttribute('open');
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('keydown', this.#onKeydown);
    this.observer?.disconnect();
  }

  #measure = () => {
    document.documentElement.style.setProperty('--sf-header-height', `${this.offsetHeight}px`);
  };

  #onResize = () => {
    this.#measure();
    this.#fitNav();
  };

  #borrowed = 0;

  #restoreSplit() {
    const left = this.querySelector('.sf-header__nav--left');
    const right = this.querySelector('.sf-header__nav--right');
    while (this.#borrowed > 0 && left && right?.firstElementChild) {
      left.appendChild(right.firstElementChild);
      this.#borrowed--;
    }
  }

  /**
   * Tries each gap step until the navs stop overflowing.
   * @param {Element[]} navs - The nav elements to measure
   * @param {number} max - The merchant's configured maximum gap
   * @returns {boolean} Whether a gap that fits was found
   */
  #fitsAtSomeGap(navs, max) {
    const overflows = () => navs.some((nav) => nav.scrollWidth > nav.clientWidth + 1);
    const smallest = NAV_GAP_STEPS[NAV_GAP_STEPS.length - 1] ?? 12;

    for (const step of NAV_GAP_STEPS) {
      const value = Math.min(step, max);
      this.style.setProperty('--sf-nav-gap', `${value}px`);
      if (!overflows()) return true;
      if (value <= smallest) break;
    }
    return false;
  }

  #fitNav() {
    const left = this.querySelector('.sf-header__nav--left');
    const right = this.querySelector('.sf-header__nav--right');
    if (!left || !window.matchMedia('(min-width: 821px)').matches) return;

    if (this.clientWidth === 0) return;

    const navs = /** @type {Element[]} */ ([left, right].filter(Boolean));
    const max = parseFloat(getComputedStyle(this).getPropertyValue('--sf-nav-gap-max')) || 26;

    this.classList.remove('sf-header--nav-collapsed');
    this.#restoreSplit();

    if (this.#fitsAtSomeGap(navs, max)) return;

    if (this.dataset.navLayout === 'left' && right) {
      while (left.children.length > 1 && left.lastElementChild) {
        right.insertBefore(left.lastElementChild, right.firstChild);
        this.#borrowed++;
        if (this.#fitsAtSomeGap(navs, max)) return;
      }
    }

    this.#restoreSplit();
    this.style.setProperty('--sf-nav-gap', `${max}px`);
    this.classList.add('sf-header--nav-collapsed');
  }

  #onScroll = () => {
    this.classList.toggle('is-solid', window.scrollY > SOLIDIFY_AFTER);
  };

  /** @param {KeyboardEvent} event - The keydown event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.closeDrawer();
  };

  /**
   * Search suggestion pills and the mobile drawer's search/cart links.
   * @param {MouseEvent} event - The delegated click event
   */
  #onActionClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const suggestion = target.closest('[data-sf-search-suggest]');
    if (suggestion) {
      const input = this.querySelector('.sf-search__input');
      if (!(input instanceof HTMLInputElement)) return;

      input.value = suggestion.getAttribute('data-sf-search-suggest') || '';
      // predictive-search.js ignores input events without an inputType, so this
      // has to be a real InputEvent for the query to actually run.
      input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
      input.focus();
      return;
    }

    if (target.closest('[data-sf-drawer-search]')) {
      const search = /** @type {(Element & { showDialog?: () => void }) | null} */ (
        this.querySelector('#sf-search')
      );
      if (typeof search?.showDialog !== 'function') return;

      event.preventDefault();
      this.closeDrawer();
      search.showDialog();
      return;
    }

    if (target.closest('[data-sf-drawer-cart]')) {
      const cart = /** @type {(Element & { open?: () => void }) | null} */ (
        this.querySelector('cart-drawer-component')
      );
      if (typeof cart?.open !== 'function') return;

      event.preventDefault();
      this.closeDrawer();
      cart.open();
    }
  };

  openDrawer() {
    if (!this.drawer) return;

    this.#lastFocused = /** @type {HTMLElement | null} */ (document.activeElement);
    this.drawer.classList.add('is-open');
    this.drawer.removeAttribute('inert');
    this.scrim?.removeAttribute('hidden');
    this.querySelector('[data-sf-nav-open]')?.setAttribute('aria-expanded', 'true');
    lockScroll('sf-homepage-menu-open');
    /** @type {HTMLElement | null} */ (this.drawer.querySelector('[data-sf-nav-close]'))?.focus();
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('inert', '');
    this.scrim?.setAttribute('hidden', '');
    this.querySelector('[data-sf-nav-open]')?.setAttribute('aria-expanded', 'false');
    unlockScroll('sf-homepage-menu-open');
    this.#lastFocused?.focus();
  }
}

class PromoModal extends HTMLElement {
  /** @type {number | undefined} */
  #timer;
  #opened = false;

  /** @type {Element | null} */
  dialog = null;

  connectedCallback() {
    this.dialog = this.querySelector('[data-sf-promo-dialog]');
    if (!(this.dialog instanceof HTMLDialogElement)) return;

    for (const button of this.querySelectorAll('[data-sf-promo-close]')) {
      button.addEventListener('click', () => this.close());
    }

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('close', () => {
      this.#opened = false;
      unlockScroll('sf-homepage-modal-open');
      this.#remember();
    });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#onEditorDeselect);
      return;
    }

    if (this.#alreadySeen()) return;

    this.#arm();
  }

  disconnectedCallback() {
    this.#disarm();
    document.removeEventListener('shopify:section:select', this.#onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#onEditorDeselect);
  }

  /** @param {Event} event - The Theme Editor section select event */
  #onEditorSelect = (event) => {
    const sectionId = /** @type {CustomEvent} */ (event).detail?.sectionId;
    if (sectionId && this.closest(`#shopify-section-${sectionId}`)) this.open();
  };

  #onEditorDeselect = () => this.close();

  #storageKey() {
    return this.dataset.storageKey || 'sf-promo';
  }

  #alreadySeen() {
    const frequency = this.dataset.frequency;
    if (frequency === 'always') return false;

    try {
      const store = frequency === 'persistent' ? window.localStorage : window.sessionStorage;
      return store.getItem(this.#storageKey()) === '1';
    } catch {
      return false;
    }
  }

  #remember() {
    if (this.dataset.frequency === 'always') return;

    try {
      const store = this.dataset.frequency === 'persistent' ? window.localStorage : window.sessionStorage;
      store.setItem(this.#storageKey(), '1');
    } catch {
    }
  }

  #arm() {
    switch (this.dataset.trigger) {
      case 'delay':
        this.#timer = window.setTimeout(() => this.open(), (Number(this.dataset.delay) || 8) * 1000);
        break;

      case 'exit':
        if (window.matchMedia('(hover: hover)').matches) {
          document.addEventListener('mouseout', this.#onExitIntent);
        } else {
          this.#timer = window.setTimeout(() => this.open(), (Number(this.dataset.delay) || 8) * 1000);
        }
        break;

      default:
        window.addEventListener('scroll', this.#onScroll, { passive: true });
    }
  }

  #disarm() {
    if (this.#timer) window.clearTimeout(this.#timer);
    window.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('mouseout', this.#onExitIntent);
  }

  #onScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const depth = (window.scrollY / scrollable) * 100;
    if (depth >= (Number(this.dataset.scrollDepth) || 25)) this.open();
  };

  /** @param {MouseEvent} event - The mouseout event */
  #onExitIntent = (event) => {
    if (event.relatedTarget || event.clientY > 0) return;
    this.open();
  };

  open() {
    if (this.#opened || !(this.dialog instanceof HTMLDialogElement)) return;

    this.#opened = true;
    this.#disarm();
    lockScroll('sf-homepage-modal-open');
    this.dialog.showModal();
  }

  close() {
    if (this.dialog instanceof HTMLDialogElement && this.dialog.open) this.dialog.close();
  }
}

const QUICK_ADD_RESET_DELAY = 1600;

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

/**
 * The theme's storefront routes, when the theme global is available.
 * @returns {{ cart_add_url?: string, cart_url?: string }}
 */
function themeRoutes() {
  return typeof Theme !== 'undefined' && Theme?.routes ? Theme.routes : {};
}

/**
 * Adds a homepage quick-add form's product to the cart.
 * @param {HTMLFormElement} form - The quick-add form
 */
async function submitQuickAdd(form) {
  const button = form.querySelector('button[type="submit"]');
  const label = form.querySelector('[data-sf-quick-add-label]');
  const originalLabel = label?.textContent ?? '';

  if (button instanceof HTMLButtonElement) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  try {
    const formData = new FormData(form);
    const sections = cartSectionIds();
    if (sections) formData.append('sections', sections);

    const routes = themeRoutes();

    const response = await fetch(routes.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Add to cart failed');
    }

    const cartResponse = await fetch(`${routes.cart_url ?? '/cart'}.js`);
    const cart = cartResponse.ok ? await cartResponse.json() : undefined;

    form.dispatchEvent(
      createCartAddEvent(cart, form.dataset.sourceId || 'homepage-quick-add', {
        source: 'homepage-quick-add',
        // The theme's cart-icon treats itemCount from non-product-form
        // sources as the cart's TOTAL count, not the amount added.
        itemCount: cart?.item_count ?? 1,
        productId: String(result.product_id ?? ''),
        variantId: String(result.variant_id ?? ''),
        sections: result.sections,
      })
    );

    if (label) label.textContent = form.dataset.addedLabel || 'Added ✓';
  } catch (error) {
    console.error(error);
    if (label) label.textContent = 'Try again';
  } finally {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }

    if (label) {
      window.setTimeout(() => {
        label.textContent = originalLabel;
      }, QUICK_ADD_RESET_DELAY);
    }
  }
}

const boundRoots = new WeakSet();

/** @param {Document | Element} [scope] - The scope to bind within */
function bindQuickAdd(scope = document) {
  for (const root of homepageRoots(scope)) {
    if (boundRoots.has(root)) continue;
    boundRoots.add(root);

    root.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-sf-quick-add]')) return;

      event.preventDefault();
      submitQuickAdd(form);
    });
  }
}

/** @type {Record<string, CustomElementConstructor>} */
const homepageElements = {
  'sf-announcement-bar': AnnouncementBar,
  'sf-site-header': SiteHeader,
  'sf-hero': HeroSlideshow,
  'sf-pillars': PillarExplorer,
  'sf-video-feature': VideoFeature,
  'sf-video-marquee': VideoMarquee,
  'sf-promo-modal': PromoModal,
};

for (const [name, constructor] of Object.entries(homepageElements)) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

/** @param {Document | Element} [scope] - The scope to initialise */
function initHomepage(scope = document) {
  initReveals(scope);
  bindQuickAdd(scope);
}

initHomepage();

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof Element) initHomepage(target);
});

// This file is always loaded with <script type="module">. The empty export
// marks it as a module so its top-level names stay file-scoped.
export {};
